import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Table, Tag, Space, Modal, message, Col } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import FilterPanel from '../../../components/common/FilterPanel';
import StoreSelect from '../../../components/common/StoreSelect';
import type { WaveRuleDTO, QueryWaveRulePageRequest } from '../../../types/wave/waveRule';
import type { Paging } from '../../../types/common';
import * as waveRuleService from '../../../services/wave/waveRuleService';
import {
  WaveRuleStatusOptions,
  WaveRuleStatus,
  WaveExecutionModeOptions,
} from '../../../constants/wave';
import WaveRuleForm from './components/WaveRuleForm';
import WaveRuleDetail from './components/WaveRuleDetail';

const WaveRuleList: React.FC = () => {
  const { t } = useTranslation();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<WaveRuleDTO[]>([]);
  const [pagination, setPagination] = useState<Paging>({ currentPage: 1, pageSize: 20, totalCount: 0 });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Drawer states
  const [formVisible, setFormVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [editingRuleCode, setEditingRuleCode] = useState<string | null>(null);
  const [viewingRuleCode, setViewingRuleCode] = useState<string | null>(null);

  // Load data
  const loadData = async (params: QueryWaveRulePageRequest) => {
    setLoading(true);
    try {
      const response = await waveRuleService.pageWaveRules(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination(response.data.paging);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Initialize
  useEffect(() => {
    loadData({ currentPage: 1, pageSize: 20 });
  }, []);

  // Search
  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    const params: QueryWaveRulePageRequest = {
      currentPage: 1,
      pageSize: pagination.pageSize,
      ruleCode: values.ruleCode,
      ruleName: values.ruleName,
      applyStoreCode: values.applyStoreCode,
      status: values.status,
    };
    loadData(params);
  };

  // Reset
  const handleReset = () => {
    searchForm.resetFields();
    loadData({ currentPage: 1, pageSize: pagination.pageSize });
  };

  // Pagination change
  const handlePageChange = (page: number, pageSize: number) => {
    const values = searchForm.getFieldsValue();
    const params: QueryWaveRulePageRequest = {
      currentPage: page,
      pageSize,
      ruleCode: values.ruleCode,
      ruleName: values.ruleName,
      applyStoreCode: values.applyStoreCode,
      status: values.status,
    };
    loadData(params);
  };

  // Create
  const handleCreate = () => {
    setEditingRuleCode(null);
    setFormVisible(true);
  };

  // Edit
  const handleEdit = (record: WaveRuleDTO) => {
    setEditingRuleCode(record.ruleCode);
    setFormVisible(true);
  };

  // View detail
  const handleViewDetail = (record: WaveRuleDTO) => {
    setViewingRuleCode(record.ruleCode);
    setDetailVisible(true);
  };

  // Trigger wave (REMOVED as requested)
  // const handleTrigger = (record: WaveRuleDTO) => { ... };

  // Enable (batch support)
  const handleEnableBatch = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('common.message.pleaseSelectData'));
      return;
    }
    Modal.confirm({
      title: t('wave.waveRule.list.actions.enable'),
      icon: <ExclamationCircleOutlined />,
      content: t('wave.waveRule.list.confirm.enable', { count: selectedRowKeys.length }),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          for (const ruleCode of selectedRowKeys as string[]) {
            await waveRuleService.enableWaveRule(ruleCode);
          }
          message.success(t('wave.waveRule.list.message.enableSuccess'));
          setSelectedRowKeys([]);
          loadData({ currentPage: pagination.currentPage, pageSize: pagination.pageSize });
        } catch (error) {
          message.error(t('common.message.operationFailed'));
        }
      },
    });
  };

  // Disable (batch support)
  const handleDisableBatch = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('common.message.pleaseSelectData'));
      return;
    }
    Modal.confirm({
      title: t('wave.waveRule.list.actions.disable'),
      icon: <ExclamationCircleOutlined />,
      content: t('wave.waveRule.list.confirm.disable'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          for (const ruleCode of selectedRowKeys as string[]) {
            await waveRuleService.disableWaveRule(ruleCode);
          }
          message.success(t('wave.waveRule.list.message.disableSuccess'));
          setSelectedRowKeys([]);
          loadData({ currentPage: pagination.currentPage, pageSize: pagination.pageSize });
        } catch (error) {
          message.error(t('common.message.operationFailed'));
        }
      },
    });
  };

  // Batch delete
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('common.message.pleaseSelectData'));
      return;
    }

    Modal.confirm({
      title: t('wave.waveRule.list.actions.batchDelete'),
      icon: <ExclamationCircleOutlined />,
      content: t('wave.waveRule.list.confirm.batchDelete', { count: selectedRowKeys.length }),
      okText: t('common.confirm'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const ruleCodes = selectedRowKeys as string[];
          const response = await waveRuleService.deleteWaveRules(ruleCodes);
          if (response.success) {
            message.success(t('wave.waveRule.list.message.deleteSuccess'));
            setSelectedRowKeys([]);
            loadData({ currentPage: pagination.currentPage, pageSize: pagination.pageSize });
          } else {
            message.error(response.errorMessage || t('common.message.operationFailed'));
          }
        } catch (error) {
          message.error(t('common.message.operationFailed'));
        }
      },
    });
  };

  // Form success callback
  const handleFormSuccess = () => {
    setFormVisible(false);
    loadData({ currentPage: pagination.currentPage, pageSize: pagination.pageSize });
  };

  // Format date
  const formatDate = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('YYYY-MM-DD') : '-';
  };

  // Format datetime
  const formatDateTime = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  // Render status tag
  const renderStatusTag = (status: WaveRuleStatus) => {
    const option = WaveRuleStatusOptions.find(opt => opt.value === status);
    if (!option) return status;
    return <Tag color={option.color}>{t(option.label)}</Tag>;
  };

  // Delete single record
  const handleDelete = (record: WaveRuleDTO) => {
    Modal.confirm({
      title: t('wave.waveRule.list.actions.delete'),
      icon: <ExclamationCircleOutlined />,
      content: t('wave.waveRule.list.confirm.delete'),
      okText: t('common.confirm'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await waveRuleService.deleteWaveRules([record.ruleCode]);
          if (response.success) {
            message.success(t('wave.waveRule.list.message.deleteSuccess'));
            loadData({ currentPage: pagination.currentPage, pageSize: pagination.pageSize });
          } else {
            message.error(response.errorMessage || t('common.message.operationFailed'));
          }
        } catch (error) {
          message.error(t('common.message.operationFailed'));
        }
      },
    });
  };

  // Table columns
  const columns = [
    {
      title: t('wave.waveRule.list.table.ruleCode'),
      dataIndex: 'ruleCode',
      key: 'ruleCode',
      width: 140,
      fixed: 'left' as const,
      render: (text: string, record: WaveRuleDTO) => (
        <a onClick={() => handleViewDetail(record)}>{text}</a>
      ),
    },
    {
      title: t('wave.waveRule.list.table.executionMode'),
      dataIndex: 'executionMode',
      key: 'executionMode',
      width: 140,
      render: (val: string) => {
        const option = WaveExecutionModeOptions.find(opt => opt.value === val);
        return option ? t(option.label) : val || '-';
      },
    },
    {
      title: t('wave.waveRule.list.table.ruleName'),
      dataIndex: 'ruleName',
      key: 'ruleName',
      width: 200,
    },
    {
      title: t('wave.waveRule.list.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: renderStatusTag,
    },
    {
      title: t('wave.waveRule.list.table.effectiveStartDate'),
      dataIndex: 'effectiveStartDate',
      key: 'effectiveStartDate',
      width: 120,
      render: formatDate,
    },
    {
      title: t('wave.waveRule.list.table.effectiveEndDate'),
      dataIndex: 'effectiveEndDate',
      key: 'effectiveEndDate',
      width: 120,
      render: (value: number | null) => value ? formatDate(value) : t('common.label.permanent'),
    },
    {
      title: t('wave.waveRule.list.table.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: formatDateTime,
    },
    {
      title: t('wave.waveRule.list.table.actions'),
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      align: 'left' as const,
      render: (_: any, record: WaveRuleDTO) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
            {t('wave.waveRule.list.actions.detail')}
          </Button>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>
            {t('wave.waveRule.list.actions.edit')}
          </Button>
          <Button type="link" size="small" danger onClick={() => handleDelete(record)}>
            {t('wave.waveRule.list.actions.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  // Search form fields
  const renderSearchFields = () => (
    <>
      <Col span={6}>
        <Form.Item name="ruleCode" label={t('wave.waveRule.list.search.ruleCode')}>
          <Input
            placeholder={t('wave.waveRule.list.search.placeholder.ruleCode')}
            allowClear
          />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="ruleName" label={t('wave.waveRule.list.search.ruleName')}>
          <Input
            placeholder={t('wave.waveRule.list.search.placeholder.ruleName')}
            allowClear
          />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="applyStoreCode" label={t('wave.waveRule.list.search.storeCode')}>
          <StoreSelect placeholder={t('wave.waveRule.list.search.placeholder.storeCode')} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="status" label={t('wave.waveRule.list.search.status')}>
          <Select
            placeholder={t('wave.waveRule.list.search.placeholder.status')}
            allowClear
            options={WaveRuleStatusOptions.map(opt => ({
              label: t(opt.label),
              value: opt.value,
            }))}
          />
        </Form.Item>
      </Col>
    </>
  );

  // Row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Search Panel */}
      <FilterPanel
        form={searchForm}
        onFilter={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        {renderSearchFields()}
      </FilterPanel>

      {/* Action Bar */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Space>
          <Button danger onClick={handleBatchDelete}>
            {t('wave.waveRule.list.actions.batchDelete')}
          </Button>
          <Button type="primary" onClick={handleEnableBatch}>
            {t('wave.waveRule.list.actions.enable')}
          </Button>
          <Button onClick={handleDisableBatch}>
            {t('wave.waveRule.list.actions.disable')}
          </Button>
          {selectedRowKeys.length > 0 && (
            <span style={{ marginLeft: 8 }}>
              {t('picking.pickingTask.list.selection.selected', { count: selectedRowKeys.length })}
            </span>
          )}
        </Space>
        <Button type="primary" onClick={handleCreate}>
          {t('wave.waveRule.list.actions.create')}
        </Button>
      </div>

      {/* Table */}
      <Table
        rowKey="ruleCode"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowSelection={rowSelection}
        scroll={{ x: 1600, y: 'calc(100vh - 420px)' }}
        pagination={{
          current: pagination.currentPage,
          pageSize: pagination.pageSize,
          total: pagination.totalCount,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => t('common.pagination.total', { total }),
          onChange: handlePageChange,
        }}
      />

      {/* Form Drawer */}
      {formVisible && (
        <WaveRuleForm
          visible={formVisible}
          ruleCode={editingRuleCode}
          onSuccess={handleFormSuccess}
          onCancel={() => setFormVisible(false)}
        />
      )}

      {/* Detail Drawer */}
      {detailVisible && viewingRuleCode && (
        <WaveRuleDetail
          visible={detailVisible}
          ruleCode={viewingRuleCode}
          onClose={() => setDetailVisible(false)}
        />
      )}
    </div>
  );
};

export default WaveRuleList;
